import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { GoEye } from "react-icons/go";
import { RiDeleteBinLine } from "react-icons/ri";
import { motion } from "framer-motion";
import { fadeUp } from "animations/variants";
import { parseLocalDateTime } from "Util/timeChangeHelperSafari";

function OrderTable({ datas = [] }) {
  const Head = "text-xs font-semibold px-6 py-2 uppercase";
  const Text = "px-5 text-sm py-3 leading-6 whitespace-nowrap";
  const badge = "py-1 px-2 rounded-full text-xs font-semibold";

  /* ================= SAFE ITEMS ================= */
  const renderItems = (items = []) => {
    if (!items.length) return <p className="text-gray-400 italic">No items</p>;

    return items.map((item) => (
      <div key={item._id} className="flex items-start gap-2 mb-1">
        <div className="w-2 h-2 bg-neonYellow rounded-full mt-2" />
        <p className="truncate">
          {item.title}
          <span className="text-gray-400 text-xs"> x{item.qty}</span>
        </p>
      </div>
    ));
  };

  /* ================= DATE ================= */
  const formatDateTime = (dateStr) => {
    if (!dateStr) return "---";

    const date = parseLocalDateTime(dateStr);
    if (isNaN(date)) return dateStr;

    return new Intl.DateTimeFormat("en-NZ", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      hour: "numeric",
      minute: "2-digit",
    }).format(date);
  };

  /* ================= SORT ================= */
  const sortedData = useMemo(() => {
    return [...datas].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
    );
  }, [datas]);

  /* ================= PAGINATION ================= */
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const totalPages = Math.ceil(sortedData.length / rowsPerPage) || 1;
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentData = sortedData.slice(startIndex, startIndex + rowsPerPage);

  /* ================= STATUS LOGIC ================= */

  const getOrderStatus = (order) => {
    if (!order) return "pending";

    // ❌ cancelled always wins
    if (order.orderStatus === "cancelled") return "cancelled";

    // ✅ already completed → keep it
    if (order.orderStatus === "completed") return "completed";

    const now = new Date();
    const created = new Date(order.createdAt);

    const hours = (now - created) / (1000 * 60 * 60);

    // ⏱ auto complete after 24h
    if (hours > 24) return "completed";

    // ✅ otherwise use real backend status
    return order.orderStatus || "pending";
  };

  const getPaymentStatus = (order) => {
    return order?.payments?.paymentStatus || "pending";
  };

  /* ================= STYLES ================= */

  const STATUS_STYLES = {
    pending: {
      badge: "bg-gray-100 text-gray-600 border border-gray-300",
    },
    preparing: {
      badge: "bg-yellowlight/30 text-yellowdark border border-yellowdark",
    },
    ready: {
      badge: "bg-blue-100 text-blue-700 border border-blue-400",
    },
    completed: {
      badge: "bg-green-100 text-green-700 border border-green-400",
    },
    cancelled: {
      badge: "bg-red-100 text-red-600 border border-red-400",
    },
  };

  const PAYMENT_STYLES = {
    paid: {
      badge: "bg-green-500/10 text-green-500 border border-green-400",
    },
    pending: {
      badge: "text-neonYellow border border-gray-600",
    },
  };

  const formatLabel = (text) =>
    text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

  /* ================= ROW ================= */
  const Rows = ({ order, index }) => {
    const orderStatus = getOrderStatus(order);
    const paymentStatus = getPaymentStatus(order);

    return (
      <motion.tr
        variants={fadeUp}
        initial="hidden"
        animate="show"
        custom={index}
        className="hover:bg-neutral-100 transition text-text"
      >
        {/* DATE */}
        <td className={`${Text} text-center`}>
          {formatDateTime(order.createdAt)}
        </td>

        {/* CUSTOMER */}
        <td className={`${Text} text-center`}>
          <div className="text-sm">
            <p>{order.customer_info?.firstname}</p>
            <p className="text-gray-400 text-xs">
              {order.customer_info?.phoneNumber}
            </p>
          </div>
        </td>

        {/* ITEMS */}
        <td className={`${Text}`}>{renderItems(order.orderItems)}</td>

        {/* ORDER STATUS */}
        <td className={`${Text} text-center`}>
          <span
            className={`${badge} ${
              STATUS_STYLES[orderStatus]?.badge
            } inline-block min-w-[90px] text-center`}
          >
            {formatLabel(orderStatus)}
          </span>
        </td>

        {/* PAYMENT STATUS */}
        <td className={`${Text} text-center`}>
          <span
            className={`${badge} ${
              PAYMENT_STYLES[paymentStatus]?.badge
            } inline-block min-w-[90px] text-center`}
          >
            {formatLabel(paymentStatus)}
          </span>
        </td>

        {/* TOTAL */}
        <td className={`${Text} text-center font-semibold text-neonYellow`}>
          ${order.totalPrice}
        </td>

        {/* ACTION */}
        <td className={`${Text}`}>
          <div className="flex justify-center items-center gap-2">
            <button className="border border-red-500 text-red-500 w-6 h-6 flex items-center justify-center rounded">
              <RiDeleteBinLine />
            </button>

            <Link
              to={`/orderdetail/${order._id}`}
              className="border border-neonYellow text-neonYellow w-6 h-6 flex items-center justify-center rounded"
            >
              <GoEye />
            </Link>
          </div>
        </td>
      </motion.tr>
    );
  };

  return (
    <div className="w-full">
      <div className="overflow-x-auto scrollbar-thin">
        <table className="min-w-full border-2 border-gray-300">
          <thead>
            <tr className="text-navy font-semibold border-2 border-gray-300">
              <th className={`${Head} text-center`}>Order Date</th>
              <th className={`${Head} text-center`}>Customer</th>
              <th className={`${Head} text-center`}>Items</th>
              <th className={`${Head} text-center`}>Order</th>
              <th className={`${Head} text-center`}>Payment</th>
              <th className={`${Head} text-center`}>Total</th>
              <th className={`${Head} text-center`}>Action</th>
            </tr>
          </thead>

          <motion.tbody
            initial="hidden"
            animate="show"
            variants={{
              show: {
                transition: {
                  staggerChildren: 0.05,
                },
              },
            }}
            className="divide-y divide-gray-300"
          >
            {currentData.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center py-6 text-neonYellow">
                  No orders found
                </td>
              </tr>
            ) : (
              currentData.map((order, index) => (
                <Rows key={order._id} order={order} index={index} />
              ))
            )}
          </motion.tbody>
        </table>
      </div>

      {/* PAGINATION */}
      <div className="flex justify-center mt-6 gap-2">
        <button
          onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
          className="text-neonYellow px-3"
        >
          ‹
        </button>

        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
          <button
            key={page}
            onClick={() => setCurrentPage(page)}
            className={`px-3 py-1 rounded-full ${
              currentPage === page ? "bg-neonYellow text-navy" : "text-white"
            }`}
          >
            {page}
          </button>
        ))}

        <button
          onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
          className="text-neonYellow px-3"
        >
          ›
        </button>
      </div>
    </div>
  );
}

export default OrderTable;
